<template>
  <v-container fluid class="pa-3 mt5-dashboard">
    <v-row dense>
      <!-- LEFT: MARKET WATCH -->
      <v-col cols="12" md="3">
        <v-card :color="cardBg" class="rounded-lg dashboard-card" :class="{ 'fullscreen-chart': isFullscreen }"
          outlined>
          <div class="panel-header">
            <span>Market Watch</span>
          </div>

          <div class="market-table-wrap">
            <table class="market-table">
              <thead>
                <tr>
                  <th>Instrument</th>
                  <th>Bid</th>
                  <th>Ask</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in marketWatch" :key="item.symbol"
                  :class="{ activeRow: selectedSymbol === item.symbol }" @click="changeSymbol(item.symbol)">
                  <td>{{ item.symbol }}</td>
                  <td :class="item.bidDir === 'up' ? 'up-text' : item.bidDir === 'down' ? 'down-text' : ''">
                    {{ formatPrice(item.bid, item.symbol) }}
                  </td>
                  <td :class="item.askDir === 'up' ? 'up-text' : item.askDir === 'down' ? 'down-text' : ''">
                    {{ formatPrice(item.ask, item.symbol) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="mini-panel pa-3">
            <div class="d-flex justify-space-between align-center mb-2">
              <strong>Ticks</strong>
              <v-icon small color="grey lighten-1">mdi-pulse</v-icon>
            </div>

            <div class="tick-list">
              <div v-for="(tick, i) in recentTicks" :key="i" class="tick-row">
                <span>{{ tick.time }}</span>
                <span :class="tick.dir === 'up' ? 'up-text' : 'down-text'">{{ tick.price }}</span>
              </div>
            </div>
          </div>
        </v-card>
      </v-col>

      <!-- CENTER: CHART + TRADE -->
      <v-col cols="12" md="6">
        <v-card :color="cardBg" class="rounded-lg dashboard-card" outlined>
          <div class="panel-header d-flex align-center justify-space-between">
            <div class="d-flex align-center">
              <span class="mr-3">{{ selectedSymbol }}</span>
              <v-chip x-small dark color="indigo" class="mr-2">M1</v-chip>
              <v-chip x-small dark color="teal">Live</v-chip>
            </div>
            <div class="d-flex align-center">
              <v-btn icon small @click="resetChartData">
                <v-icon small>mdi-refresh</v-icon>
              </v-btn>
              <v-btn icon small @click="toggleFullscreen">
                <v-icon small>
                  {{ isFullscreen ? 'mdi-fullscreen-exit' : 'mdi-fullscreen' }}
                </v-icon>
              </v-btn>
            </div>
          </div>

          <div class="chart-top-stats px-3 py-2">
            <div class="stat-box">
              <small>Bid</small>
              <strong class="up-text">{{ currentBidFormatted }}</strong>
            </div>
            <div class="stat-box">
              <small>Ask</small>
              <strong class="down-text">{{ currentAskFormatted }}</strong>
            </div>
            <div class="stat-box">
              <small>Spread</small>
              <strong>{{ spreadFormatted }}</strong>
            </div>
            <div class="stat-box">
              <small>High</small>
              <strong>{{ highFormatted }}</strong>
            </div>
            <div class="stat-box">
              <small>Low</small>
              <strong>{{ lowFormatted }}</strong>
            </div>
          </div>

          <div id="tv_chart_container" class="chart-container"></div>

          <!-- TRADE PANEL -->
          <div class="trade-panel pa-3">
            <div class="trade-title mb-3">Trade</div>

            <v-row dense>
              <v-col cols="12" sm="4">
                <fieldset class="custom-fieldset">
                  <legend>Symbol</legend>
                  <v-select v-model="tradeForm.symbol" :items="symbols" dense solo flat hide-details
                    class="custom-input" @change="changeSymbol" />
                </fieldset>
              </v-col>

              <v-col cols="12" sm="4">
                <fieldset class="custom-fieldset">
                  <legend>Order Type</legend>
                  <v-select v-model="tradeForm.orderType" :items="['Buy', 'Sell']" dense solo flat hide-details
                    class="custom-input" />
                </fieldset>
              </v-col>

              <v-col cols="12" sm="4">
                <fieldset class="custom-fieldset">
                  <legend>Volume</legend>
                  <v-text-field v-model="tradeForm.volume" dense solo flat hide-details class="custom-input"
                    type="number" />
                </fieldset>
              </v-col>

              <v-col cols="12" sm="6">
                <fieldset class="custom-fieldset">
                  <legend>Stop Loss</legend>
                  <v-text-field v-model="tradeForm.stopLoss" dense solo flat hide-details class="custom-input" />
                </fieldset>
              </v-col>

              <v-col cols="12" sm="6">
                <fieldset class="custom-fieldset">
                  <legend>Take Profit</legend>
                  <v-text-field v-model="tradeForm.takeProfit" dense solo flat hide-details class="custom-input" />
                </fieldset>
              </v-col>
            </v-row>

            <div class="d-flex mt-3">
              <v-btn large class="mr-2 sell-btn flex-grow-1" @click="placeMockOrder('Sell')">
                Sell
              </v-btn>

              <v-btn large class="buy-btn flex-grow-1" @click="placeMockOrder('Buy')">
                Buy
              </v-btn>
            </div>
          </div>
        </v-card>
      </v-col>

      <!-- RIGHT: DEPTH OF MARKET -->
      <v-col cols="12" md="3">
        <v-card :color="cardBg" class="rounded-lg dashboard-card" outlined>
          <div class="panel-header">
            <span>Depth of Market</span>
          </div>

          <div class="dom-top pa-3">
            <v-btn block class="buy-btn mb-2">
              Buy {{ currentBidFormatted }}
            </v-btn>
            <v-btn block class="sell-btn">
              Sell {{ currentAskFormatted }}
            </v-btn>
          </div>

          <div class="dom-table-wrap px-3 pb-3">
            <table class="dom-table">
              <thead>
                <tr>
                  <th>Price</th>
                  <th>Volume</th>
                  <th>Side</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, i) in domRows" :key="i">
                  <td>{{ row.price }}</td>
                  <td>{{ row.volume }}</td>
                  <td :class="row.side === 'BUY' ? 'up-text' : 'down-text'">
                    {{ row.side }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- BOTTOM TERMINAL -->
    <v-row dense class="mt-2">
      <v-col cols="12">
        <v-card :color="cardBg" class="rounded-lg dashboard-card" outlined>
          <v-tabs v-model="activeTab" background-color="transparent" color="primary" dark class="terminal-tabs">
            <v-tab>Trade</v-tab>
            <v-tab>Exposure</v-tab>
            <v-tab>History</v-tab>
            <v-tab>News</v-tab>
            <v-tab>Journal</v-tab>
          </v-tabs>

          <v-tabs-items v-model="activeTab" class="terminal-body">
            <!-- TRADE TAB -->
            <v-tab-item>
              <div class="terminal-table-wrap">
                <table class="terminal-table">
                  <thead>
                    <tr>
                      <th>Order</th>
                      <th>Time</th>
                      <th>Type</th>
                      <th>Symbol</th>
                      <th>Volume</th>
                      <th>Open Price</th>
                      <th>S/L</th>
                      <th>T/P</th>
                      <th>Current</th>
                      <th>Profit</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="order in openOrders" :key="order.id">
                      <td>{{ order.id }}</td>
                      <td>{{ order.time }}</td>
                      <td :class="order.type === 'Buy' ? 'up-text' : 'down-text'">{{ order.type }}</td>
                      <td>{{ order.symbol }}</td>
                      <td>{{ order.volume }}</td>
                      <td>{{ order.openPrice }}</td>
                      <td>{{ order.stopLoss }}</td>
                      <td>{{ order.takeProfit }}</td>
                      <td>{{ order.currentPrice }}</td>
                      <td :class="parseFloat(order.profit) >= 0 ? 'up-text' : 'down-text'">
                        {{ order.profit }}
                      </td>
                    </tr>
                    <tr v-if="!openOrders.length">
                      <td colspan="10" class="text-center py-4">No open trades</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </v-tab-item>

            <!-- EXPOSURE -->
            <v-tab-item>
              <div class="pa-4 exposure-box">
                <div class="mb-2"><strong>Total Buy Volume:</strong> {{ exposure.buy }}</div>
                <div class="mb-2"><strong>Total Sell Volume:</strong> {{ exposure.sell }}</div>
                <div><strong>Net Exposure:</strong> {{ exposure.net }}</div>
              </div>
            </v-tab-item>

            <!-- HISTORY -->
            <v-tab-item>
              <div class="terminal-table-wrap">
                <table class="terminal-table">
                  <thead>
                    <tr>
                      <th>Ticket</th>
                      <th>Time</th>
                      <th>Type</th>
                      <th>Symbol</th>
                      <th>Volume</th>
                      <th>Close Price</th>
                      <th>Profit</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in historyOrders" :key="item.id">
                      <td>{{ item.id }}</td>
                      <td>{{ item.time }}</td>
                      <td>{{ item.type }}</td>
                      <td>{{ item.symbol }}</td>
                      <td>{{ item.volume }}</td>
                      <td>{{ item.closePrice }}</td>
                      <td :class="parseFloat(item.profit) >= 0 ? 'up-text' : 'down-text'">{{ item.profit }}</td>
                    </tr>
                    <tr v-if="!historyOrders.length">
                      <td colspan="7" class="text-center py-4">No closed trades</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </v-tab-item>

            <!-- NEWS -->
            <v-tab-item>
              <div class="pa-4 journal-list">
                <div class="journal-item" v-for="(item, idx) in newsItems" :key="idx">
                  {{ item }}
                </div>
              </div>
            </v-tab-item>

            <!-- JOURNAL -->
            <v-tab-item>
              <div class="pa-4 journal-list">
                <div class="journal-item" v-for="(item, idx) in journalLogs" :key="idx">
                  {{ item }}
                </div>
              </div>
            </v-tab-item>
          </v-tabs-items>
        </v-card>
      </v-col>
    </v-row>

    <!-- ⚡ FULLSCREEN DIALOG -->
    <v-dialog v-model="isFullscreen" fullscreen hide-overlay>
      <v-card>
        <v-toolbar dark color="primary">
          <v-btn icon @click="isFullscreen = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>{{ selectedSymbol }} Chart</v-toolbar-title>
        </v-toolbar>

        <v-card-text>
          <div id="tv_chart_fullscreen" class="chart-container"></div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>


export default {
  name: "Mt5DashboardMock",
  data() {
    return {
      chart: null,
      candleSeries: null,
      volumeSeries: null,
      priceTimer: null,
      isFullscreen: false,
      activeTab: 0,
      selectedSymbol: "EURUSD",
      symbols: ["EURUSD", "GBPUSD", "USDJPY", "BTCUSD", "XAUUSD", "US30"],

      marketWatch: [
        { symbol: "EURUSD", bid: 1.2145, ask: 1.2147, bidDir: "", askDir: "" },
        { symbol: "GBPUSD", bid: 1.2742, ask: 1.2745, bidDir: "", askDir: "" },
        { symbol: "USDJPY", bid: 151.245, ask: 151.265, bidDir: "", askDir: "" },
        { symbol: "BTCUSD", bid: 64250.12, ask: 64255.12, bidDir: "", askDir: "" },
        { symbol: "XAUUSD", bid: 3032.55, ask: 3033.15, bidDir: "", askDir: "" },
        { symbol: "US30", bid: 39210.5, ask: 39212.5, bidDir: "", askDir: "" },
      ],

      domRows: [],
      recentTicks: [],

      tradeForm: {
        symbol: "EURUSD",
        orderType: "Buy",
        volume: 1.0,
        stopLoss: "",
        takeProfit: "",
      },

      openOrders: [],
      historyOrders: [],
      newsItems: [
        "ECB member speech scheduled later today.",
        "USD remains mixed against majors in early session.",
        "Gold holds firm near intraday highs.",
      ],
      journalLogs: [
        "System initialized successfully.",
        "Live feed connected (mock).",
        "Chart updated with new tick.",
      ],

      candles: [],
      lastCandleTime: null,
      lastPrice: 1.2145,
    };
  },

  computed: {
    cardBg() {
      return this.$vuetify.theme.dark ? "#172233" : "#e8f1f8";
    },

    currentSymbolData() {
      return this.marketWatch.find((x) => x.symbol === this.selectedSymbol) || this.marketWatch[0];
    },

    currentBidFormatted() {
      return this.formatPrice(this.currentSymbolData.bid, this.selectedSymbol);
    },

    currentAskFormatted() {
      return this.formatPrice(this.currentSymbolData.ask, this.selectedSymbol);
    },

    spreadFormatted() {
      const spread = this.currentSymbolData.ask - this.currentSymbolData.bid;
      return this.selectedSymbol === "BTCUSD" || this.selectedSymbol === "US30"
        ? spread.toFixed(2)
        : spread.toFixed(5);
    },

    highFormatted() {
      if (!this.candles.length) return "-";
      const high = Math.max(...this.candles.slice(-30).map((c) => c.high));
      return this.formatPrice(high, this.selectedSymbol);
    },

    lowFormatted() {
      if (!this.candles.length) return "-";
      const low = Math.min(...this.candles.slice(-30).map((c) => c.low));
      return this.formatPrice(low, this.selectedSymbol);
    },

    exposure() {
      const buy = this.openOrders
        .filter((o) => o.type === "Buy")
        .reduce((sum, o) => sum + Number(o.volume), 0);

      const sell = this.openOrders
        .filter((o) => o.type === "Sell")
        .reduce((sum, o) => sum + Number(o.volume), 0);

      return {
        buy: buy.toFixed(2),
        sell: sell.toFixed(2),
        net: (buy - sell).toFixed(2),
      };
    },
  },

  mounted() {
    this.generateDomRows();
    this.initFirstCandle();
    this.startFakeFeed();
    window.addEventListener("resize", this.handleResize);
    this.loadTradingView();
  },

  beforeDestroy() {
    if (this.priceTimer) clearInterval(this.priceTimer);
    window.removeEventListener("resize", this.handleResize);

    if (this.chart) {
      this.chart.remove();
      this.chart = null;
    }
  },

  methods: {

    loadTradingView() {
      if (window.TradingView) {
        this.createTVChart();
      } else {
        const script = document.createElement("script");
        script.src = "https://s3.tradingview.com/tv.js";
        script.onload = () => this.createTVChart();
        document.head.appendChild(script);
      }
    },
    resetChartData() {
      this.candles = [];
      this.lastCandleTime = null;
      setTimeout(() => {
        this.createTVChart();
      }, 100);

      this.addJournal("Chart data reset");
    },
    toggleFullscreen() {
  this.isFullscreen = !this.isFullscreen;

  // 🔥 Proper scroll control
  if (this.isFullscreen) {
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
  } else {
    document.documentElement.style.overflow = "auto";
    document.body.style.overflow = "auto";
  }

  this.$nextTick(() => {
    setTimeout(() => {
      this.createTVChart(
        this.isFullscreen ? "tv_chart_fullscreen" : "tv_chart_container"
      );
    }, 200);
  });
},
    initFirstCandle() {
      const base = this.getBasePrice(this.selectedSymbol);
      const now = Math.floor(Date.now() / 1000);
      const time = Math.floor(now / 60) * 60;

      const candle = {
        time,
        open: base,
        high: base,
        low: base,
        close: base,
      };

      this.candles = [candle];
      this.lastCandleTime = time;
    },

    createTVChart(containerId = 'tv_chart_container') {
      document.getElementById(containerId).innerHTML = "";

      new window.TradingView.widget({
        container_id: containerId,
        width: "100%",
        height: this.isFullscreen ? window.innerHeight - 50 : 400,
        symbol: this.getTVSymbol(this.selectedSymbol),
        interval: "1",
        timezone: "Etc/UTC",
        theme: "dark",
        style: "1",
        toolbar_bg: "#0f172a",
        enable_publishing: false,
      });
    },

    getTVSymbol(symbol) {
      const map = {
        EURUSD: "FX:EURUSD",
        GBPUSD: "FX:GBPUSD",
        USDJPY: "FX:USDJPY",
        BTCUSD: "BINANCE:BTCUSDT",
        XAUUSD: "OANDA:XAUUSD",
        US30: "FOREXCOM:DJI",
      };
      return map[symbol] || "FX:EURUSD";
    },
    handleResize() {
      if (this.chart && this.$refs.chartContainer) {
        this.chart.applyOptions({
          width: this.$refs.chartContainer.clientWidth,
        });
      }
    },



    startFakeFeed() {
      if (this.priceTimer) clearInterval(this.priceTimer);

      this.priceTimer = setInterval(() => {
        this.mockPriceUpdate();
        this.updateOpenOrdersProfit();
        this.generateDomRows();
      }, 1500);
    },

    mockPriceUpdate() {
      if (!this.candles.length) return;

      const last = this.candles[this.candles.length - 1];

      let newClose = last.close + this.randomFloat(-0.0008, 0.0008, this.selectedSymbol);
      newClose = this.normalizePrice(newClose, this.selectedSymbol);

      const nowSec = Math.floor(Date.now() / 1000);
      const currentMinute = Math.floor(nowSec / 60) * 60;

      if (currentMinute > this.lastCandleTime) {
        const newCandle = {
          time: currentMinute,
          open: last.close,
          high: Math.max(last.close, newClose),
          low: Math.min(last.close, newClose),
          close: newClose,
        };

        this.candles.push(newCandle);
        this.lastCandleTime = currentMinute;
      } else {
        last.high = Math.max(last.high, newClose);
        last.low = Math.min(last.low, newClose);
        last.close = newClose;
      }

      this.updateSelectedSymbolPrice(newClose);
      this.addRecentTick(newClose);
    },

    updateSelectedSymbolPrice(lastClose) {
      const item = this.marketWatch.find((x) => x.symbol === this.selectedSymbol);
      if (!item) return;

      const oldBid = item.bid;
      const oldAsk = item.ask;

      const spread = this.getSpread(this.selectedSymbol);
      item.bid = this.normalizePrice(lastClose, this.selectedSymbol);
      item.ask = this.normalizePrice(lastClose + spread, this.selectedSymbol);

      item.bidDir = item.bid > oldBid ? "up" : item.bid < oldBid ? "down" : "";
      item.askDir = item.ask > oldAsk ? "up" : item.ask < oldAsk ? "down" : "";
    },

    addRecentTick(price) {
      const d = new Date();
      const tick = {
        time: d.toLocaleTimeString(),
        price: this.formatPrice(price, this.selectedSymbol),
        dir: Math.random() > 0.5 ? "up" : "down",
      };

      this.recentTicks.unshift(tick);
      if (this.recentTicks.length > 8) this.recentTicks.pop();
    },

    addJournal(text) {
      this.journalLogs.unshift(`${new Date().toLocaleTimeString()} - ${text}`);
      if (this.journalLogs.length > 15) this.journalLogs.pop();
    },

    generateDomRows() {
      const base = this.currentSymbolData.bid;
      const rows = [];
      const step = this.getDomStep(this.selectedSymbol);

      for (let i = 4; i >= 1; i--) {
        rows.push({
          price: this.formatPrice(base - i * step, this.selectedSymbol),
          volume: (Math.random() * 5 + 0.5).toFixed(2),
          side: "BUY",
        });
      }

      for (let i = 0; i < 5; i++) {
        rows.push({
          price: this.formatPrice(base + i * step, this.selectedSymbol),
          volume: (Math.random() * 5 + 0.5).toFixed(2),
          side: "SELL",
        });
      }

      this.domRows = rows;
    },

    placeMockOrder(type) {
      const currentPrice = type === "Buy"
        ? this.currentSymbolData.ask
        : this.currentSymbolData.bid;

      const order = {
        id: Date.now(),
        time: new Date().toLocaleTimeString(),
        type,
        symbol: this.tradeForm.symbol,
        volume: Number(this.tradeForm.volume).toFixed(2),
        openPrice: this.formatPrice(currentPrice, this.tradeForm.symbol),
        stopLoss: this.tradeForm.stopLoss || "-",
        takeProfit: this.tradeForm.takeProfit || "-",
        currentPrice: this.formatPrice(currentPrice, this.tradeForm.symbol),
        profit: "0.00",
      };

      this.openOrders.unshift(order);
      this.addJournal(`${type} order placed on ${order.symbol} @ ${order.openPrice}`);
      this.activeTab = 0;
    },

    updateOpenOrdersProfit() {
      this.openOrders = this.openOrders.map((order) => {
        if (order.symbol !== this.selectedSymbol) return order;

        const market = this.marketWatch.find((m) => m.symbol === order.symbol);
        const current = order.type === "Buy" ? market.bid : market.ask;

        const pointProfit = order.type === "Buy"
          ? current - parseFloat(order.openPrice)
          : parseFloat(order.openPrice) - current;

        let multiplier = 100000;
        if (order.symbol === "BTCUSD") multiplier = 1;
        if (order.symbol === "XAUUSD") multiplier = 100;
        if (order.symbol === "US30") multiplier = 10;
        if (order.symbol === "USDJPY") multiplier = 1000;

        const profit = pointProfit * Number(order.volume) * multiplier;

        return {
          ...order,
          currentPrice: this.formatPrice(current, order.symbol),
          profit: profit.toFixed(2),
        };
      });
    },

    changeSymbol(symbol) {
      this.selectedSymbol = symbol;
      this.tradeForm.symbol = symbol;

      this.generateDomRows();

      // reload TradingView
      setTimeout(() => {
        this.createTVChart();
      }, 100);
    },

    getBasePrice(symbol) {
      const prices = {
        EURUSD: 1.2145,
        GBPUSD: 1.2742,
        USDJPY: 151.245,
        BTCUSD: 64250.12,
        XAUUSD: 3032.55,
        US30: 39210.5,
      };
      return prices[symbol] || 1.0;
    },

    getSpread(symbol) {
      const spreads = {
        EURUSD: 0.0002,
        GBPUSD: 0.0003,
        USDJPY: 0.02,
        BTCUSD: 5.0,
        XAUUSD: 0.6,
        US30: 2.0,
      };
      return spreads[symbol] || 0.0002;
    },

    getDomStep(symbol) {
      const steps = {
        EURUSD: 0.0001,
        GBPUSD: 0.0001,
        USDJPY: 0.01,
        BTCUSD: 2.0,
        XAUUSD: 0.1,
        US30: 1.0,
      };
      return steps[symbol] || 0.0001;
    },

    normalizePrice(value, symbol) {
      if (symbol === "BTCUSD" || symbol === "US30") return Number(value.toFixed(2));
      if (symbol === "XAUUSD") return Number(value.toFixed(2));
      if (symbol === "USDJPY") return Number(value.toFixed(3));
      return Number(value.toFixed(5));
    },

    formatPrice(value, symbol) {
      if (symbol === "BTCUSD" || symbol === "US30") return Number(value).toFixed(2);
      if (symbol === "XAUUSD") return Number(value).toFixed(2);
      if (symbol === "USDJPY") return Number(value).toFixed(3);
      return Number(value).toFixed(5);
    },

    randomFloat(min, max, symbol) {
      const raw = Math.random() * (max - min) + min;

      if (symbol === "BTCUSD") return raw * 500;
      if (symbol === "US30") return raw * 100;
      if (symbol === "XAUUSD") return raw * 10;
      if (symbol === "USDJPY") return raw * 10;

      return raw;
    },

  },
};

</script>

<style scoped>
.mt5-dashboard {
  background: #0b1220;

  height: 100vh;              /* full screen height */
  overflow-y: auto;           /* page scroll enable */
  overflow-x: hidden;

  display: flex;
  flex-direction: column;
}
.mt5-dashboard .v-row {
  flex: 0 0 auto;
}
.dashboard-card {
  border: 1px solid rgba(255,255,255,0.06) !important;
  box-shadow: 0 8px 24px rgba(0,0,0,0.25);
  overflow: auto;
  display: flex;
  flex-direction: column;
  height: 100%;
}
.market-table-wrap {
  max-height: 300px;
  overflow-y: auto;
}
.terminal-body {
  height: 420px;
  overflow-y: auto;
}
.dom-table-wrap {
  max-height: 420px;
  overflow-y: auto;
}

.terminal-table-wrap {
  max-height: 320px;
  overflow: auto;
}

.panel-header {
  padding: 12px 16px;
  font-size: 18px;
  font-weight: 700;
  color: #e2e8f0;
  background: linear-gradient(90deg, #172033 0%, #101827 100%);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.chart-container {
  width: 100%;
  height: 400px;
  background: #0f172a;
}

.chart-top-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  background: #121b2d;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.stat-box {
  min-width: 90px;
  color: #cbd5e1;
  display: flex;
  flex-direction: column;
}

.market-table-wrap,
.dom-table-wrap,
.terminal-table-wrap {
  overflow-x: auto;
}

.market-table,
.dom-table,
.terminal-table {
  width: 100%;
  border-collapse: collapse;
}

.market-table th,
.market-table td,
.dom-table th,
.dom-table td,
.terminal-table th,
.terminal-table td {
  padding: 10px 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  color: #e2e8f0;
  font-size: 13px;
  text-align: left;
  white-space: nowrap;
}

.market-table thead th,
.dom-table thead th,
.terminal-table thead th {
  background: #121b2d;
  font-weight: 700;
}

.market-table tbody tr {
  cursor: pointer;
  transition: 0.2s ease;
}

.market-table tbody tr:hover {
  background: rgba(59, 130, 246, 0.08);
}

.activeRow {
  background: rgba(59, 130, 246, 0.16);
}

.up-text {
  color: #22c55e !important;
  font-weight: 700;
}

.down-text {
  color: #ef4444 !important;
  font-weight: 700;
}

.mini-panel {
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  background: #101827;
}

.tick-list {
  max-height: 220px;
  overflow-y: auto;
}

.tick-row {
  display: flex;
  justify-content: space-between;
  color: #cbd5e1;
  font-size: 13px;
  padding: 5px 0;
  border-bottom: 1px dashed rgba(255, 255, 255, 0.05);
}

.trade-panel {
  background: #101827;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.trade-title {
  color: #e2e8f0;
  font-size: 18px;
  font-weight: 700;
}

.custom-fieldset {
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 8px;
  padding: 0 8px 6px;
  min-height: 62px;
  background: #0f172a;
}

.custom-fieldset legend {
  padding: 0 6px;
  font-size: 12px;
  color: #94a3b8;
}

.custom-input {
  margin-top: -6px;
}

.sell-btn {
  background: #dc2626 !important;
  color: #fff !important;
  font-weight: 700;
}

.buy-btn {
  background: #16a34a !important;
  color: #fff !important;
  font-weight: 700;
}

.dom-top {
  background: #101827;
}

.terminal-tabs {
  background: linear-gradient(90deg, #172033 0%, #101827 100%);
}

.terminal-body {
  background: #0f172a;
}

.exposure-box,
.journal-list {
  color: #e2e8f0;
  min-height: 180px;
}

.journal-item {
  padding: 8px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.d-flex.mt-3 .v-btn {
  width: 100%;
}

.d-flex.mt-3 .v-btn {
  flex: 1;
}

.fullscreen-chart {
  position: relative;
  /* remove fixed */
  width: 100%;
  height: 100%;
}

.fullscreen-chart #tv_chart_container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw !important;
  height: calc(100vh - 50px);
  /* leave room for toolbar if needed */
  z-index: 9999;
}

.dashboard-card {
  min-height: 100%;
}
@media (max-width: 960px) {
  .chart-container {
    height: 300px;
  }
}
</style>